import { HStack, Tabs, Text } from '@chakra-ui/react';
import CockpitDialog from '@components/cockpit/CockpitDialog';
import { CockpitEditorLayout } from '@components/cockpit/layouts/CockpitEditorLayout';
import { Breadcrumb } from '@components/cockpit/panels/Breadcrumb';
import ModifiedTag from '@components/common/form/ModifiedField';
import { Form } from '@components/ui/Form';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import useValidation from '@hooks/useValidation';
import { hasRole, selectActiveGroupOrganization, selectActiveSite } from '@redux/authent/authent.selectors';
import { useCreateDroneMutation, useUpdateDroneMutation } from '@services/utm/drone.api';
import { areSelectedFieldsEqual, haveSameUniqueElements } from '@utils/common.utils';
import { droneSchemaValidation } from '@validations/utm.schema';
import { Formik } from 'formik';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { RoleEnum } from '@/types/authent/roles.types';
import { Organization } from '@/types/config/config.types';
import { Drone, DroneClearanceEnum, PartialDrone } from '@/types/utm/drone.types';

import { useUtmContext } from '../UtmManagement';
import DroneHabilitationTab from './DroneHabilitationTab';
import DronePilotsTab from './DronePilotsTab';
import DronePublicTab from './DronePublicTab';
import DroneStrategicTab from './DroneStrategicTab';

export const emptyDrone = (site: string, organization: Organization | null): PartialDrone => ({
  serialNumber: '',
  manufacturer: null,
  model: null,
  pilots: [],
  adsbNumber: '',
  aeroscopeNumber: '',
  skyEyeAntennaNumber: '',
  holodroneNumber: '',
  groundStationNumber: '',
  site,
  strategicDrones: [],
  strategicDrone: {
    name: '',
    site,
    weight: null,
    range: null,
    autonomy: null,
    strategicClearance:
      organization?.organizationParameter.defaultClearance ?? DroneClearanceEnum.FULL_CLEARANCE_WITHIN_FLIGHTPLAN,
    safetyDistance: 150,
    criticalDistance: 50,
  },
});

export default function DroneFormContainer() {
  const { formatMessage } = useIntl();
  const organization = useAppSelector(selectActiveGroupOrganization);

  const { editedItem, setEditedItem } = useUtmContext();
  const activeSite = useAppSelector(selectActiveSite);
  const empty = emptyDrone(activeSite?.code ?? 'local', organization);
  const drone = (editedItem?.value as PartialDrone | Drone | null | undefined) ?? empty;
  const validDrone = drone.strategicDrone === null ? { ...drone, strategicDrone: empty.strategicDrone } : drone;
  const [open, setOpen] = useState(false);
  const [createDrone, { isLoading: isLoadingCreate }] = useCreateDroneMutation();
  const [updateDrone, { isLoading: isLoadingUpdate }] = useUpdateDroneMutation();
  const isNewDrone = validDrone.id === undefined;
  const validationSchema = useValidation(droneSchemaValidation);

  const canAccessPilots = useAppSelector((state) => hasRole(state, RoleEnum.ACCESS_PILOT));

  const handleSubmit = (values: PartialDrone) => {
    if (isNewDrone) {
      createDrone({
        ...values,
        code: uuidv4(),
      } as Drone)
        .unwrap()
        .then((drone) => {
          setOpen(false);
          createToast(
            formatMessage({ id: 'components.utm.drones.message.add' }, { name: drone.functionalName }),
            ToastStatusEnum.SUCCESS,
          );
          setEditedItem(null);
        });
    } else {
      updateDrone({
        ...values,
      } as Drone)
        .unwrap()
        .then((drone) => {
          setOpen(false);
          createToast(
            formatMessage({ id: 'components.utm.drones.message.update' }, { name: drone.functionalName }),
            ToastStatusEnum.SUCCESS,
          );
          setEditedItem(null);
        });
    }
  };

  return (
    <Formik
      initialValues={validDrone as PartialDrone}
      validationSchema={validationSchema}
      validateOnBlur
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ initialValues, isValid, submitForm, values }) => {
        const hasDroneStrategicTabChanged = !areSelectedFieldsEqual(values, initialValues, [
          'strategicDrone.name',
          'strategicDrone.safetyDistance',
          'strategicDrone.criticalDistance',
        ]);

        const hasPublicDroneTabChanged = !areSelectedFieldsEqual(values, initialValues, [
          'model',
          'manufacturer',
          'serialNumber',
          'adsbNumber',
          'aeroscopeNumber',
          'skyEyeAntennaNumber',
          'groundStationNumber',
          'holodroneNumber',
          'strategicDrone.weight',
          'strategicDrone.range',
          'strategicDrone.autonomy',
        ]);

        const hasFleetAndHabilitationTabChanged = !areSelectedFieldsEqual(values, initialValues, [
          'strategicDrone.strategicClearance',
        ]);
        const hasPilotsTabChanged = !haveSameUniqueElements(values.pilots!, initialValues.pilots!);

        const hasChanged = () => {
          return (
            hasPublicDroneTabChanged ||
            hasDroneStrategicTabChanged ||
            hasFleetAndHabilitationTabChanged ||
            hasPilotsTabChanged
          );
        };
        return (
          <Form style={{ width: '100%', height: '100%' }}>
            <CockpitEditorLayout
              dialog={
                <CockpitDialog
                  open={open}
                  isLoading={isLoadingCreate || isLoadingUpdate}
                  confirmLabel={formatMessage({ id: 'global.save' })}
                  cancelLabel={formatMessage({ id: 'global.cancel' })}
                  onConfirm={submitForm}
                  onCancel={() => setOpen(false)}
                >
                  <Text color="neutral.100" width="100%" whiteSpace="pre-line">
                    <FormattedMessage
                      id={`components.utm.drones.popup.${isNewDrone ? 'descriptionSaveNew' : 'descriptionSave'}`}
                      values={{ name: values.strategicDrone.name }}
                    />
                  </Text>
                </CockpitDialog>
              }
              breadcrumbs={
                <Breadcrumb
                  items={[
                    {
                      label: formatMessage({ id: 'components.utm.drones.title' }),
                      action: () => setEditedItem(null),
                    },
                    {
                      label: isNewDrone
                        ? formatMessage({
                            id: 'components.utm.drones.newDrone',
                          })
                        : (drone.functionalName ?? formatMessage({ id: 'components.utm.drones.unknown' })),
                    },
                  ]}
                />
              }
              onBack={() => setEditedItem(null)}
              onSave={() => {
                if (!isValid || !hasChanged()) {
                  return;
                }
                setOpen(true);
              }}
              isSaving={isLoadingCreate || isLoadingUpdate}
              isSaveDisabled={!isValid || !hasChanged()}
            >
              <Tabs.Root
                orientation="vertical"
                defaultValue="public"
                size="md"
                lazyMount
                variant="vertical"
                userSelect="none"
              >
                <Tabs.List>
                  <Tabs.Trigger value="public">
                    <HStack width="100%">
                      <FormattedMessage id="components.utm.drones.tab.public.title" />
                      {hasPublicDroneTabChanged && <ModifiedTag />}
                    </HStack>
                  </Tabs.Trigger>
                  <Tabs.Trigger value="strategic">
                    <HStack width="100%">
                      <FormattedMessage id="components.utm.drones.tab.strategic.title" />
                      {hasDroneStrategicTabChanged && <ModifiedTag />}
                    </HStack>
                  </Tabs.Trigger>
                  <Tabs.Trigger value="habilitation">
                    <HStack width="100%">
                      <FormattedMessage id="components.utm.drones.tab.habilitation.title" />
                      {hasFleetAndHabilitationTabChanged && <ModifiedTag />}
                    </HStack>
                  </Tabs.Trigger>
                  {canAccessPilots && (
                    <Tabs.Trigger value="pilots">
                      <HStack width="100%">
                        <FormattedMessage id="components.utm.drones.tab.pilots.title" />
                        {hasPilotsTabChanged && <ModifiedTag />}
                      </HStack>
                    </Tabs.Trigger>
                  )}
                </Tabs.List>
                <Tabs.ContentGroup backgroundColor="neutral.700">
                  <Tabs.Content value="public">
                    <DronePublicTab />
                  </Tabs.Content>
                  <Tabs.Content value="strategic">
                    <DroneStrategicTab />
                  </Tabs.Content>
                  <Tabs.Content value="habilitation">
                    <DroneHabilitationTab />
                  </Tabs.Content>
                  {canAccessPilots && (
                    <Tabs.Content value="pilots">
                      <DronePilotsTab />
                    </Tabs.Content>
                  )}
                </Tabs.ContentGroup>
              </Tabs.Root>
            </CockpitEditorLayout>
          </Form>
        );
      }}
    </Formik>
  );
}
